import React, { createContext, useEffect, useMemo, useState, ReactNode, useRef } from 'react';
import { store } from '../../../stores/store';
import CommonStore from '../../../stores/commonStore';

interface ReportContextType {
    reportData: any[];
    setReportData: React.Dispatch<React.SetStateAction<any[]>>;
    filtersVisible: boolean;
    setFiltersVisible: React.Dispatch<React.SetStateAction<boolean>>;
    reportNavVisible: boolean;
    setReportNavVisible: React.Dispatch<React.SetStateAction<boolean>>;
    dataLastRefreshed: string | null;
    setDataLastRefreshed: React.Dispatch<React.SetStateAction<string | null>>;
    memorizedListUpdates: boolean;
    setMemorizedListUpdates: React.Dispatch<React.SetStateAction<boolean>>;
    favoriteListUpdates: boolean;
    setFavoriteListUpdates: React.Dispatch<React.SetStateAction<boolean>>;
    isPinReport: boolean;
    setIsPinReport: React.Dispatch<React.SetStateAction<boolean>>;
    memorizedData?: object;
    setMemorizedData?: (data: object) => void;
    reportFilter: object;
    setReportFilter: (data: object) => void;
    reportCurrentPageNumber: number;
    setReportCurrentPageNumber: React.Dispatch<React.SetStateAction<number>>;
    reportPageSize: number;
    setReportPageSize: React.Dispatch<React.SetStateAction<number>>;
    reportTotalCount: number;
    setReportTotalCount: React.Dispatch<React.SetStateAction<number>>;
    isUpdatedMemReport: boolean;
    setIsUpdatedMemReport: React.Dispatch<React.SetStateAction<boolean>>;
    currentReportId?: string;
    setCurrentReportId?: React.Dispatch<React.SetStateAction<string | undefined>>;
    metricCardsLoading?: boolean;
    setMetricCardsLoading?: React.Dispatch<React.SetStateAction<boolean>>;
    isEditingMemorized?: boolean;
    setIsEditingMemorized?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const getDefaultReportFilter = (commonStore: CommonStore) => ({
  hospitalId: [String(commonStore?.hospitalId)],
  departmentId: [String(commonStore?.departmentId)],
  expiringBucket: 30,
  consigned: 2,
  orderBy: '',
  queryBuilder: [],
});

export const ReportContext = createContext<ReportContextType | undefined>(undefined);

export const ReportProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [reportData, setReportData] = useState<any[]>([]);
    const [filtersVisible, setFiltersVisible] = useState<boolean>(false);
    const [reportNavVisible, setReportNavVisible] = useState<boolean>(true);
    const [dataLastRefreshed, setDataLastRefreshed] = useState<string | null>(null);
    const [favoriteListUpdates, setFavoriteListUpdates] = useState<boolean>(false);
    const [memorizedListUpdates, setMemorizedListUpdates] = useState<boolean>(false);
    const [isPinReport, setIsPinReport] = useState<boolean>(false);
    const [reportFilter, setReportFilter] = useState<object>(() => getDefaultReportFilter(store.commonStore));
    const [memorizedData, setMemorizedData] = useState<object>({});
    const [reportCurrentPageNumber, setReportCurrentPageNumber] = useState<number>(1);
    const [reportPageSize, setReportPageSize] = useState<number>(20);
    const [reportTotalCount, setReportTotalCount] = useState<number>(0);
    const [isUpdatedMemReport, setIsUpdatedMemReport] = useState<boolean>(false);
    const [currentReportId, setCurrentReportId] = useState<string | undefined>(undefined);
    const [metricCardsLoading, setMetricCardsLoading] = useState<boolean>(false);
    const [isEditingMemorized, setIsEditingMemorized] = useState<boolean>(false);
    // Ref used to remember last hospital/department pair
    const resetRef = useRef<string | undefined>(undefined);

    // currentReportId is set by StandardReportLayout via context setter; no router dependency here

    // Removed: clearing sessionStorage on 'navigate' caused filters to reset in some MF hosts.
    // We now only reset persisted filters when hospital/department truly changes.

    // Derive a stable key for the current report route to scope persistence
    const reportKey = useMemo(() => {
        const id = currentReportId || 'unknown';
        return `reportFilter:${id}`;
    }, [currentReportId]);

    // Helper to detect if current load was a full page reload (keep filters)
    const isReloadNavigation = () => {
        try {
            const entries = performance.getEntriesByType('navigation') as Array<PerformanceNavigationTiming & { type?: string }>;
            if (entries && entries.length > 0) {
                return entries[0].type === 'reload';
            }
            // Fallback for deprecated API
            const nav: any = (performance as any).navigation;
            // 1 == TYPE_RELOAD
            if (nav && typeof nav.type === 'number') return nav.type === 1;
        } catch { /* ignore */ }
        return false;
    };

    // On report route activation, decide whether to hydrate or clear persisted filters
    useEffect(() => {
        const keepFromStorage = isReloadNavigation();
        if (keepFromStorage) {
            try {
                const raw = sessionStorage.getItem(reportKey);
                if (raw) {
                    const parsed = JSON.parse(raw);
                    if (parsed && typeof parsed === 'object') {
                        setReportFilter(parsed);
                    }
                }
            } catch { /* ignore parse/storage errors */ }
        } else {
            // Normal navigation into the report (not a reload): reset to defaults and clear any prior persisted filters
            try { sessionStorage.removeItem(reportKey); } catch { /* ignore */ }
            setReportFilter(getDefaultReportFilter(store.commonStore));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportKey]);

    // Persist reportFilter when it changes for the current report
    useEffect(() => {
        try {
            sessionStorage.setItem(reportKey, JSON.stringify(reportFilter));
        } catch { /* ignore storage quota or unavailable */ }
    }, [reportKey, reportFilter]);

    // Reset session storage-backed filters only when hospital/department truly changes
    useEffect(() => {
        const { hospitalId, departmentId } = store.commonStore;
        if (hospitalId === undefined || departmentId === undefined) return;

        const curr = `${hospitalId}:${departmentId}`;

        // Prefer prior value from ref; fall back to sessionStorage so it survives remounts
        let prev = resetRef.current;
        if (prev === undefined) {
            try {
                prev = sessionStorage.getItem('reportContextKey') || undefined;
            } catch { /* ignore storage errors */ }
        }

        const changed = prev !== undefined && prev !== curr;

        // Update trackers
        resetRef.current = curr;
        try { sessionStorage.setItem('reportContextKey', curr); } catch { /* ignore storage errors */ }

        if (!changed) {
            // First mount or same context: do not clear persisted filters
            return;
        }

        try {
            // Clear all persisted report filters to honor new defaults
            for (let i = sessionStorage.length - 1; i >= 0; i--) {
                const key = sessionStorage.key(i);
                if (key && key.startsWith('reportFilter:')) {
                    sessionStorage.removeItem(key);
                }
            }
        } catch { /* ignore storage errors */ }

        // Reset the in-memory filter to new defaults for the new context
        setReportFilter(getDefaultReportFilter(store.commonStore));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [store.commonStore.hospitalId, store.commonStore.departmentId]);


    return (
        <ReportContext.Provider value={{
            reportData, setReportData, filtersVisible, setFiltersVisible, reportNavVisible, setReportNavVisible, dataLastRefreshed, setDataLastRefreshed, memorizedListUpdates, setMemorizedListUpdates,
            favoriteListUpdates, setFavoriteListUpdates, isPinReport, setIsPinReport, reportCurrentPageNumber, setReportCurrentPageNumber,
            reportFilter, setReportFilter, reportPageSize, setReportPageSize, reportTotalCount, setReportTotalCount, memorizedData, setMemorizedData,
            isUpdatedMemReport, setIsUpdatedMemReport, currentReportId, setCurrentReportId, metricCardsLoading, setMetricCardsLoading,
            isEditingMemorized, setIsEditingMemorized
        }}>
            {children}
        </ReportContext.Provider>
    );
};
